import React, { useState } from "react";
import Drawer from "@mui/material/Drawer";
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@heroicons/react/solid";
import useReadWithGet from "../../hooks/read/useReadWithGetMethod";
import { useParams } from "react-router-dom";
import { formatTime } from "../../utility";
import Moment from "react-moment";
import usePut from "../../hooks/usePut";
import toast from "react-hot-toast";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import Pagination from "../../components/pagination";
import useRead from "../../hooks/useRead";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { Button } from "../../components/ui";
import RoundedContainer from "../../components/containers/RoundedContainer";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

const AdvanceEmploymentEditing = ({ open, onClose, mode }) => {
  const { company_employee_id, username } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedShiftToDelete, setSelectedShiftToDelete] = useState(null);

  const { list } = useRead({
    url: "shift_listing",
    method: "GET",
    initialData: {
      limit: 50,
      searchQuery: "",
    },
  });
  const {
    list: userShiftListing,
    paramsObject,
    setGetListParams,
  } = useReadWithGet({
    url: "user_shift_listing",
    initialData: {
      limit: 100,
      searchQuery: "",
      company_employee_id: company_employee_id,
      page_number: 0,
    },
  });
  const { handleAuth: submitEmployeeShift } = usePut({
    url: "user_shift_update",
    refreshUrl: "user_shift_listing",
    onSuccess: () => {
      toast.success("Employee Shift Updated");
      onClose();
    },
    onError: () => {
      toast.error(
        "There was error while updating shift please try again later"
      );
    },
  });
  const { handleAuth: deleteEmployeeShift } = usePut({
    url: "delete_user_shift_mapping",
    refreshUrl: "user_shift_listing",
    onSuccess: () => {
      toast.success("Employee Shift Deleted Successfully");
      setIsDeleteModalOpen(false);
      setSelectedShiftToDelete(null);
    },
    onError: () => {
      toast.error(
        "There was an error while deleting shift, please try again later"
      );
      setIsDeleteModalOpen(false);
    },
  });

  const [shiftState, setShiftState] = useState({
    shift_id: "",
    company_employee_id: company_employee_id,
    effect_from: "",
  });
  const handleChangeShift = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    setShiftState((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const handleUpdateShift = () => {
    const { shift_id, effect_from } = shiftState;
    if (!shift_id || !effect_from) {
      return alert("Please Enter Shift and Effect From");
    }
    submitEmployeeShift.mutate(shiftState);
  };
  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(data);
  };

  const handleDeleteClick = (shift) => {
    setSelectedShiftToDelete(shift);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedShiftToDelete) {
      deleteEmployeeShift.mutate({ id: selectedShiftToDelete.id });
    }
  };

  return (
    <>
      <Drawer anchor={"right"} open={open} onClose={onClose}>
        {(submitEmployeeShift.isLoading || deleteEmployeeShift.isLoading) && (
          <div className=" fixed w-full h-full top-0 left-0 flex items-center justify-center bg-[#ffffffab]">
            <PrimaryLoader />
          </div>
        )}
        <div
          className="h-full bg-white p-8"
          style={{ width: "calc(100vw - 600px)" }}
        >
          <form className=" space-y-8">
            <div>
              <div className="flex items-center justify-between space-x-3 pb-5 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <ArrowLeftIcon
                    className=" text-gray-700 w-8 h-8 cursor-pointer"
                    onClick={onClose}
                  />
                  <p className="text-gray-700 text-xl font-semibold">
                    Shift details for {username}
                  </p>
                </div>
                <div>
                  {mode !== "resigned" && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={handleUpdateShift}
                    >
                      Update Shift
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-x-5 py-5">
                <div>
                  <div className="">
                    <label className="label">Current Shift: </label>
                    <span className="block font-semibold text-base bg-red-100 text-red-600 py-2 rounded-md text-center">
                      {userShiftListing?.data?.current_shift_data?.shiftName}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="">
                    <label className="label">New Shift: </label>
                    <select
                      disabled={mode === "resigned"}
                      name="shift_id"
                      onChange={handleChangeShift}
                      className="input"
                    >
                      <option value={shiftState.shift_id}>
                        --Select Shift--
                      </option>
                      {list.data?.data?.map((shiftData) => {
                        return (
                          <option key={shiftData.id} value={shiftData.id}>
                            {shiftData.shiftName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <div>
                    <label className="label">Effect From </label>
                    <input
                      disabled={mode === "resigned"}
                      value={shiftState.effect_from}
                      onChange={handleChangeShift}
                      type="date"
                      name="effect_from"
                      className="input"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <p className="text-gray-700 text-3xl font-semibold mb-4">
                  Previous Assigned Shifts
                </p>
              </div>
              {userShiftListing.data?.data.length <= 0 ? (
                ""
              ) : (
                <>
                  <div className="max-w-full overflow-x-auto mt-4">
                    <Table className="table-auto w-full">
                      <TableHead>
                        <TableHeaderCell className="w-[20%] text-center">
                          Shift
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[16%] text-center">
                          Shift In-Time
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[16%] text-center">
                          Shift Out-Time
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[18%] text-center">
                          Effected From
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[18%] text-center">
                          Effected Till
                        </TableHeaderCell>
                        <TableHeaderCell className="w-[12%] text-center">
                          Actions
                        </TableHeaderCell>
                      </TableHead>
                      <TableBody>
                        {userShiftListing.data?.data.map((item) => {
                          return (
                            <TableRow key={item.id}>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.shiftName || "----"}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.shiftInTime
                                  ? formatTime(item.shiftInTime)
                                  : "---"}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.shiftOutTime
                                  ? formatTime(item.shiftOutTime)
                                  : "---"}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.from_date ? (
                                  <Moment
                                    unix
                                    date={item.from_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              <TableCell className="text-center text-dark font-medium text-sm">
                                {item.to_date ? (
                                  <Moment
                                    unix
                                    date={item.to_date}
                                    format="DD/MM/YYYY"
                                  />
                                ) : (
                                  "----"
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  type="button"
                                  onClick={() => handleDeleteClick(item)}
                                  variant={"ghost"}
                                  size={"icon"}
                                >
                                  <TrashIcon className="w-5 h-5 mx-auto text-gray-500 hover:text-red-600 transition-colors" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="px-5 mt-5">
                    <Pagination
                      currentPage={+paramsObject.page_number}
                      lengthofItems={userShiftListing.data?.total_count}
                      limit={paramsObject.limit}
                      onPageChange={handlePageClick}
                    />
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </Drawer>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Shift Assignment"
        description={
          selectedShiftToDelete ? (
            <>
              Are you sure you want to delete the{" "}
              <span className="font-bold text-red-600">
                {selectedShiftToDelete.shiftName}
              </span>{" "}
              shift assignment (
              <span className="font-bold text-blue-600">
                {selectedShiftToDelete.shiftInTime} -{" "}
                {selectedShiftToDelete.shiftOutTime}
              </span>
              )? This action cannot be undone.
            </>
          ) : (
            "Are you sure you want to delete this shift assignment? This action cannot be undone."
          )
        }
        confirmButtonLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default AdvanceEmploymentEditing;
